<template>
    <div class="d-flex flex-column h-100">
        <div class="flex-grow-1">
            <h2 class="donation-title text-center">Secured Donation</h2>
            <div class="list-donate list-donate--two mb-3" v-if="donationData.allow_change_amount">
                <div
                    class="chip-donate"
                    :class="{ 'chip-donate--active': donateType === 1 }"
                    @click="() => changeDonateType(1)"
                >
                    One Time
                </div>
                <div
                    class="chip-donate"
                    :class="{ 'chip-donate--active': donateType === 2 }"
                    @click="() => changeDonateType(2)"
                >
                    Monthly
                </div>
            </div>
            <div class="list-donate list-donate--three mb-3" v-if="donationData.allow_change_amount">
                <div
                    v-for="amount in donationData.amount_options"
                    :key="amount"
                    class="chip-donate"
                    :class="{ 'chip-donate--active': amount === donateAmount }"
                    @click="() => changeDonateCount(amount)"
                >
                    {{ toCurrency(amount) }}
                </div>
            </div>
            <TextField
                v-model="donateAmount"
                name="donateCount"
                input-type="2"
                classWrapper="donate-count-input"
                :disabled="!donationData.allow_change_amount"
            >
                <template #prepend-icon>
                    <span class="font-weight-bold text-secondary">$</span>
                </template>
                <template #append-icon>
                    <span class="font-weight-bold text-secondary">USD</span>
                </template>
            </TextField>
            <div class="text-secondary ml-3 mt-1 mb-3" v-if="!isNotCoveredFee">
                Plus ${{ fee }} convenience fee
            </div>
            <CheckboxField
                class="mt-2"
                v-model="isNotCoveredFee"
                name="isNotCoveredFee"
                label="I don't want to make every cent count by NOT covering the processing fee with my donation."
                input-type="2"
            />
            <SelectField
                v-if="designationItems.length"
                v-model="designation"
                name="designation"
                :options="designationItems"
                inputType="2"
                classWrapper="mb-3"
                placeholder="Select designation"
                :disabled="!donationData.allow_change_designation"
                :disable-icon="!donationData.allow_change_designation"
            />
            <CheckboxField
                v-model="isDedicatedDonation"
                name="isDedicatedDonation"
                label="Dedicate this donation"
                input-type="2"
            />
            <TextField
                v-if="isDedicatedDonation"
                v-model="dedicateTo"
                name="dedicateTo"
                input-type="2"
                classWrapper="mb-3"
                placeholder="Dedicated to..."
            />
            <TextField
                v-model="comment"
                name="comment"
                input-type="2"
                classWrapper="mb-3"
                placeholder="Comment (optional)"
            />
        </div>
        <button
            @click="submit"
            :disabled="!isValid"
            class="btn w-100 ok-btn mt-3">Donate</button>
    </div>
</template>

<script>
import TextField from '@/commons/FormElements/TextField.vue';
import CheckboxField from '@/commons/FormElements/CheckboxField.vue';
import SelectField from '@/commons/FormElements/SelectField.vue';

export default {
    name: 'SecuredDonation',
    components: {
        TextField,
        CheckboxField,
        SelectField,
    },
    props: {
        donationData: {
            type: Object,
            default: () => ({}),
        },
        donationFormData: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            donateType: 1, // 1: One month, 2: Monthly
            donateAmount: '0',
            isDedicatedDonation: false,
            isNotCoveredFee: false,
            dedicateTo: '',
            comment: '',
            designation: '',
            designationItems: []
        };
    },
    created() {
        if (this.donationData.designations.length > 0) {
            this.designationItems = this.donationData.designations.map(item => {
                return {
                    label: item.name,
                    value: item.id,
                };
            });
        }
        this.donateAmount = this.donationFormData.donateAmount ? this.donationFormData.donateAmount : this.donationData.default_amount;
        this.donateType = this.donationFormData.donateType;
        this.isDedicatedDonation = this.donationFormData.isDedicatedDonation;
        this.dedicateTo = this.donationFormData.dedicateTo;
        this.comment = this.donationFormData.comment;
        this.designation =
            this.donationFormData.designation ? this.donationFormData.designation :
                (this.donationData.default_designation_id ? this.donationData.default_designation_id : '');
    },
    watch: {
        donateAmount() {
            setTimeout(() => {
                if (this.donateAmount < this.donationData.min_amount) {
                    this.donateAmount = this.donationData.min_amount;
                }
            }, 1500);
        },
    },
    computed: {
        fee() {
            return this.donateAmount ?
                (this.donationData.transaction_fee + Math.round(parseInt(this.donateAmount) * this.donationData.discount_fee_pc) / 100).toFixed(2)
                : 0;
        },
        isValid() {
            return this.donateAmount > 0 && (this.designationItems.length > 0 ? this.designation : true)
                && (!this.isDedicatedDonation || this.dedicateTo);
        }
    },
    methods: {
        changeDonateType(type) {
            this.donateType = type;
        },
        changeDonateCount(amount) {
            this.donateAmount = amount;
        },
        toCurrency(number) {
            return `$${new Intl.NumberFormat().format(number)}`;
        },
        submit() {
            this.$emit('changeStep', 2);
            this.$emit('updateData', {
                donationType: this.donateType,
                donateAmount: this.donateAmount,
                isDedicatedDonation: this.isDedicatedDonation,
                isNotCoveredFee: this.isNotCoveredFee,
                dedicateTo: this.dedicateTo,
                comment: this.comment,
                designation: this.designation,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.list-donate {
    display: grid;
    gap: 8px;
    &--two {
        grid-template-columns: auto auto;
    }
    &--three {
        grid-template-columns: auto auto auto;
    }
}
.chip-donate {
    height: 40px;
    border-radius: 8px;
    border: 1px solid #7c7c7a;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 16px;
    font-weight: 700;
    color: #7c7c7a;
    padding-inline: 12px;
    cursor: pointer !important;
    &--active {
        color: #2178ab;
        border: 1px solid #2178ab;
        outline: #2178ab solid 1px;
    }
}
</style>
<style lang="scss">
.donate-count-input {
    input {
        color: #2178ab !important;
        font-size: 20px !important;
        font-weight: 700;
    }
}
</style>
